import React, { useState } from 'react';
import './PricingGuideOverlay.css';
import AdminRegistrationOverlay from '../registration/AdminRegistrationOverlay';
import MockPaymentOverlay from './MockPaymentOverlay';
import ContactOverlay from './ContactOverlay';

interface PricingGuideOverlayProps {
    onClose: () => void;
    isLoggedIn?: boolean;
    currentVehicleCount?: number;
}

export default function PricingGuideOverlay({ onClose, isLoggedIn = false, currentVehicleCount = 0 }: PricingGuideOverlayProps) {
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
    const [purchaseStep, setPurchaseStep] = useState<'pricing' | 'register' | 'payment'>(isLoggedIn ? 'register' : 'pricing');
    const [selectedPlanDetails, setSelectedPlanDetails] = useState({ 
        name: isLoggedIn ? '프리미엄' : '', 
        price: isLoggedIn ? 49900 : 0 
    });
    const [vehicleCount, setVehicleCount] = useState(1);
    const [showContact, setShowContact] = useState(false);

    const handleSelectPlan = (name: string, monthlyPrice: number) => {
        setSelectedPlanDetails({ name, price: monthlyPrice });
        setPurchaseStep('register');
    };

    const handleRegistrationComplete = () => {
        setPurchaseStep('payment');
    };

    const handlePaymentComplete = () => {
        setPurchaseStep('pricing');
        onClose();
        // In reality, we'd log them in or redirect to dashboard here
    };

    return (
        <div className="pricing-overlay">
            <div className="pricing-header">
                <button className="close-btn" onClick={onClose}>&times;</button>
                <div className="pricing-title">EView 서비스 요금안내</div>
                <div className="pricing-subtitle">고객님의 비즈니스 규모와 니즈에 맞는 최적의 플랜을 선택하세요.</div>
                
                <div className="billing-toggle-container">
                    <div className="billing-toggle">
                        <div 
                            className={`toggle-option ${billingCycle === 'monthly' ? 'active' : ''}`}
                            onClick={() => setBillingCycle('monthly')}
                        >
                            월간 청구
                        </div>
                        <div 
                            className={`toggle-option yearly-option ${billingCycle === 'yearly' ? 'active' : ''}`}
                            onClick={() => setBillingCycle('yearly')}
                        >
                            연간 청구
                            <span className="discount-badge">연간 결제 할인</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="pricing-categories-row">
                <div className="category-label span-2">BASIC & STANDARD</div>
                <div className="category-label span-2">PREMIUM & ENTERPRISE</div>
            </div>

            <div className="pricing-content-grid">
                <div className="pricing-column">
                    <div className="plan-card basic-plan">
                        <div className="plan-header">
                            <h3 className="plan-name">베이직 요금제</h3>
                            <p className="plan-desc">모니터링 + 리포트</p>
                            <div className="plan-price" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <div style={{ display: 'flex', gap: '6px', fontSize: '18px', fontWeight: '800', marginBottom: '-12px', zIndex: 1 }}>
                                    <span style={{ color: '#ff0000' }}>-33%</span>
                                    <span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>9,900원</span>
                                </div>
                                <div className="current-price-row">
                                    <span className="price-amount">{billingCycle === 'monthly' ? '5,900' : '4,900'}</span>원 <span className="price-unit">/ 월</span>
                                </div>
                            </div>
                            <p className="plan-target">이제 막 전기차를 도입하여 필수적인 관리를 시작하려는 '실속 고객' 향 서비스</p>
                        </div>
                        <div className="plan-features">
                            <div className="feature-group">
                                <div className="feature-group-title">[필수 모니터링 & 관리]</div>
                                <ul className="feature-list">
                                    <li>차량 상태 및 배터리 SoH 모니터링</li>
                                    <li>주행 기록 및 충전 이력 자동 관리</li>
                                    <li>배터리 교체 / 점검 필요 시점 알림</li>
                                </ul>
                            </div>
                            <div className="feature-group">
                                <div className="feature-group-title">[리포트]</div>
                                <ul className="feature-list">
                                    <li>월간 기본 운행 데이터 리포트</li>
                                </ul>
                            </div>
                        </div>
                        <div className="plan-action">
                            <button className="action-btn btn-dark" onClick={() => handleSelectPlan('베이직', billingCycle === 'monthly' ? 9900 : 8600)}>결제하기</button>
                        </div>
                    </div>
                </div>

                <div className="pricing-column">
                    <div className="plan-card standard-plan recommended">
                        <div className="recommended-badge">👑 RECOMMENDED</div>
                        <div className="plan-header">
                            <h3 className="plan-name">스탠다드 요금제</h3>
                            <p className="plan-desc">실시간 모니터링 + 리포트</p>
                            <div className="plan-price" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <div style={{ display: 'flex', gap: '6px', fontSize: '18px', fontWeight: '800', marginBottom: '-12px', zIndex: 1 }}>
                                    <span style={{ color: '#ff0000' }}>-50%</span>
                                    <span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>24,900원</span>
                                </div>
                                <div className="current-price-row">
                                    <span className="price-amount">{billingCycle === 'monthly' ? '12,900' : '10,900'}</span>원 <span className="price-unit">/ 월</span>
                                </div>
                            </div>
                            <p className="plan-target">전기차 데이터 분석을 통해 실질적인 이익을 얻고 싶은 '전문가 고객' 향 솔루션</p>
                        </div>
                        <div className="plan-features">
                            <div className="feature-group">
                                <div className="feature-group-title">[실시간 모니터링 & 최적 관리]</div>
                                <ul className="feature-list">
                                    <li>Basic Plan 기능 모두 포함</li>
                                    <li className="highlight">실시간 상태 및 배터리 모니터링</li>
                                    <li className="highlight">AI 기반 고장 예측 경고 시스템</li>
                                </ul>
                            </div>
                            <div className="feature-group">
                                <div className="feature-group-title">[리포트]</div>
                                <ul className="feature-list">
                                    <li>Basic Plan 기능 모두 포함</li>
                                    <li className="highlight">일·주간 운행 데이터 리포트</li>
                                    <li className="highlight">운영 효율 개선 리포트</li>
                                </ul>
                            </div>
                        </div>
                        <div className="plan-action">
                            <button className="action-btn btn-green" onClick={() => handleSelectPlan('스탠다드', billingCycle === 'monthly' ? 24900 : 19900)}>결제하기</button>
                        </div>
                    </div>
                </div>

                <div className="pricing-column">
                    <div className="plan-card premium-plan recommended">
                        <div className="recommended-badge">👑 RECOMMENDED</div>
                        <div className="plan-header premium-header">
                            <h3 className="plan-name">프리미엄 요금제</h3>
                            <p className="plan-desc">실시간 모니터링 + 타겟 리포트</p>
                            <div className="plan-price" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <div style={{ display: 'flex', gap: '6px', fontSize: '18px', fontWeight: '800', marginBottom: '-12px', zIndex: 1 }}>
                                    <span style={{ color: '#ff0000' }}>-60%</span>
                                    <span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>49,900원</span>
                                </div>
                                <div className="current-price-row">
                                    <span className="price-amount">{billingCycle === 'monthly' ? '19,900' : '15,900'}</span>원 <span className="price-unit">/ 월</span>
                                </div>
                            </div>
                            <p className="plan-target">차량 플릿(Fleet)을 '핵심 자산'으로 기업의 전략적 사업 목표 달성과 가치상승 목적의 '기업 고객' 향 플랫폼</p>
                        </div>
                        <div className="plan-features">
                            <div className="feature-group">
                                <div className="feature-group-title">[실시간 모니터링 & 최적 관리]</div>
                                <ul className="feature-list">
                                    <li>Standard Plan 기능 모두 포함</li>
                                </ul>
                            </div>
                            <div className="feature-group">
                                <div className="feature-group-title">[전략적 자산 관리 리포트]</div>
                                <ul className="feature-list">
                                    <li>Standard Plan 기능 모두 포함</li>
                                    <li className="highlight">ESG/ 탄소 절감 리포트 (투자자, 정부 보고용)</li>
                                    <li className="highlight">고객사 전용 리포트 대시보드 (클라우드 서버 무제한 접근)</li>
                                </ul>
                            </div>
                        </div>
                        <div className="plan-action">
                            <button className="action-btn btn-red" onClick={() => handleSelectPlan('프리미엄', billingCycle === 'monthly' ? 49900 : 34900)}>결제하기</button>
                        </div>
                    </div>

                    <div className="plan-card tco-plan prepaid">
                        <div className="plan-header tco-header">
                            <h3 className="plan-name">프리미엄 요금제 + TCO Care</h3>
                            <div className="plan-price">
                                {billingCycle === 'yearly' && (
                                    <div className="original-price-row">
                                        <span className="discount-rate">-30%</span>
                                        <span className="crossed-price">399,000원</span>
                                    </div>
                                )}
                                <div className="current-price-row">
                                    <span className="price-amount">{billingCycle === 'monthly' ? '399,000' : '279,000'}</span>원 <span className="price-unit">/대당·월</span>
                                </div>
                            </div>
                        </div>
                        <div className="plan-features">
                            <p className="plan-target">
                                [TCO Care] 5년 만기 후 전기 물류차 재제조 배터리 교체 및 부품 교체 서비스 (신차성능 기대)
                            </p>
                        </div>
                        <div className="plan-action">
                            <button className="action-btn btn-tco-blue" onClick={() => handleSelectPlan('프리미엄+TCO(선불)', billingCycle === 'monthly' ? 399000 : 279000)}>결제하기</button>
                        </div>
                    </div>
                </div>

                <div className="pricing-column">
                    <div className="plan-card enterprise-plan">
                        <div className="plan-header enterprise-header">
                            <h3 className="plan-name">엔터프라이즈 요금제</h3>
                            <p className="plan-desc">실시간 모니터링 + 타겟 리포트 + 온프레미스</p>
                            <div className="plan-price">
                                <div className="current-price-row">
                                    <span className="price-amount">문의</span>
                                </div>
                            </div>
                            <p className="plan-target">프리딕션의 핵심 기술을 자사의 시스템 및 서비스와 완벽하게 통합하여 새로운 가치를 창출하고자 하는 '기업 고객 맞춤형' 시스템</p>
                        </div>
                        <div className="plan-features enterprise-features">
                            <ul className="feature-list">
                                <li>API, SDK</li>
                                <li>기업 맞춤 브랜딩</li>
                                <li>기타 추가기능</li>
                            </ul>
                        </div>
                        <div className="plan-action">
                            <button className="action-btn btn-black" onClick={() => setShowContact(true)}>문의하기</button>
                        </div>
                    </div>

                    <div className="plan-card tco-plan postpaid">
                        <div className="plan-header tco-header">
                            <h3 className="plan-name">프리미엄 요금제 + TCO Care</h3>
                            <div className="plan-price">
                                {billingCycle === 'yearly' && (
                                    <div className="original-price-row">
                                        <span className="discount-rate">-30%</span>
                                        <span className="crossed-price">99,000원</span>
                                    </div>
                                )}
                                <div className="current-price-row">
                                    <span className="price-amount">{billingCycle === 'monthly' ? '99,000' : '69,000'}</span>원 <span className="price-unit">/대당·월</span>
                                </div>
                            </div>
                        </div>
                        <div className="plan-features">
                            <p className="plan-target">
                                [TCO Care] 5년 만기 후 후불 전기 물류차 재제조 배터리 교체 및 부품 교체 서비스 (신차성능 기대) (후불금: 20,000,000원)
                            </p>
                        </div>
                        <div className="plan-action">
                            <button className="action-btn btn-tco-purple" onClick={() => handleSelectPlan('프리미엄+TCO(후불)', billingCycle === 'monthly' ? 99000 : 69000)}>결제하기</button>
                        </div>
                    </div>
                </div>
            </div>

            {purchaseStep === 'register' && (
                <AdminRegistrationOverlay 
                    selectedPlan={selectedPlanDetails.name}
                    billingCycle={billingCycle}
                    planPrice={selectedPlanDetails.price}
                    vehicleCount={vehicleCount}
                    setVehicleCount={setVehicleCount}
                    isLoggedIn={isLoggedIn}
                    currentVehicleCount={currentVehicleCount}
                    onClose={() => isLoggedIn ? onClose() : setPurchaseStep('pricing')}
                    onRegister={handleRegistrationComplete}
                />
            )}

            {purchaseStep === 'payment' && (
                <MockPaymentOverlay 
                    selectedPlan={selectedPlanDetails.name}
                    billingCycle={billingCycle}
                    planPrice={selectedPlanDetails.price}
                    vehicleCount={vehicleCount}
                    onClose={() => isLoggedIn ? setPurchaseStep('register') : setPurchaseStep('pricing')}
                    onSuccess={handlePaymentComplete}
                />
            )}

            {showContact && (
                <ContactOverlay onClose={() => setShowContact(false)} />
            )}
        </div>
    );
}
